import React, { useCallback, useEffect, useState } from "react";
import moment from "moment";
import { endPoints } from "../../constant/Environment";
import { getAllData } from "../../Utility/API";
import Toaster from "../../components/Toast";
import Loader from "../../components/Loader";
import PageHeader from "../../components/PageHeader";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";
import { useTheme } from "@mui/material/styles";
import Box from "@mui/material/Box";
import TableFooter from "@mui/material/TableFooter";
import TablePagination from "@mui/material/TablePagination";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";

function TablePaginationActions(props) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (event) => onPageChange(event, 0);
  const handleBackButtonClick = (event) => onPageChange(event, page - 1);
  const handleNextButtonClick = (event) => onPageChange(event, page + 1);
  const handleLastPageButtonClick = (event) =>
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton onClick={handleFirstPageButtonClick} disabled={page === 0} aria-label="first page">
        {theme.direction === "rtl" ? <LastPageIcon /> : <FirstPageIcon />}
      </IconButton>
      <IconButton onClick={handleBackButtonClick} disabled={page === 0} aria-label="previous page">
        {theme.direction === "rtl" ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === "rtl" ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === "rtl" ? <FirstPageIcon /> : <LastPageIcon />}
      </IconButton>
    </Box>
  );
}

// Column id -> the sortBy key the API whitelists (see HappyHourEarningDaoImpl.SORT_COLUMNS).
const COLUMNS = [
  { id: "id", label: "ID" },
  { id: "happyHourTitle", label: "Happy Hour" },
  { id: "login", label: "Player" },
  { id: "email", label: "Email" },
  { id: "occurrenceDate", label: "Occurrence" },
  { id: "challengesPlayed", label: "Challenges" },
  { id: "baseCoins", label: "Base Coins" },
  { id: "bonusCoins", label: "Bonus Coins" },
  { id: "totalCoins", label: "Total Coins" },
  { id: "bonusDropClaimed", label: "Drop Claimed" },
  { id: "updatedOn", label: "Updated" },
];

const EMPTY_FILTERS = {
  search: "",
  fromDate: "",
  toDate: "",
};

const HappyHourEarnings = () => {
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [filters, setFilters] = useState(EMPTY_FILTERS);
  // Search is debounced so typing doesn't fire a request per keystroke.
  const [searchTerm, setSearchTerm] = useState("");

  const [sortBy, setSortBy] = useState("occurrenceDate");
  const [sortDir, setSortDir] = useState("desc");

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchEarnings = useCallback(async () => {
    const params = new URLSearchParams();
    if (filters.search !== "") params.append("search", filters.search);
    if (filters.fromDate !== "") params.append("fromDate", filters.fromDate);
    if (filters.toDate !== "") params.append("toDate", filters.toDate);
    params.append("sortBy", sortBy);
    params.append("sortDir", sortDir);

    setIsLoading(true);
    try {
      const response = await getAllData(
        `${endPoints.api.GET_HAPPY_HOUR_EARNINGS}?${params.toString()}`
      );
      const data = Array.isArray(response) ? response : response?.data || [];
      setRows(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(error);
      setRows([]);
    } finally {
      setIsLoading(false);
    }
  }, [filters, sortBy, sortDir]);

  useEffect(() => {
    fetchEarnings();
  }, [fetchEarnings]);

  // Push the typed text into the filters once typing settles.
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((prev) => (prev.search === searchTerm ? prev : { ...prev, search: searchTerm }));
      setPage(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const setFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(0);
  };

  const handleSort = (columnId) => {
    if (sortBy === columnId) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(columnId);
      setSortDir("desc");
    }
    setPage(0);
  };

  const resetFilters = () => {
    setSearchTerm("");
    setFilters(EMPTY_FILTERS);
    setSortBy("occurrenceDate");
    setSortDir("desc");
    setPage(0);
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const pagedRows =
    rowsPerPage > 0
      ? rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      : rows;

  const formatNumber = (value) =>
    value === null || value === undefined ? "-" : Number(value).toLocaleString();

  const formatDate = (value) => (value ? moment(value).format("YYYY-MM-DD") : "-");

  const formatDateTime = (value) =>
    value ? moment(value).format("YYYY-MM-DD HH:mm") : "-";

  return (
    <>
      <div className="main">
        <Toaster></Toaster>
        {isLoading && <Loader spinner={true} visible={isLoading} />}
        <div id="Sportbook" style={{ padding: "0px" }}>
          <div className="container-fluid bg-color-sportbook custom-card4">
            <div className="inner-space1">
              <PageHeader
                title="Happy Hour Earnings"
                crumb="Promotions / Happy Hours / Earnings"
              />

              <div style={{ marginTop: "10px" }}>
                <div
                  className="table_search"
                  style={{
                    display: "flex",
                    gap: "10px",
                    alignItems: "center",
                    flexWrap: "wrap",
                    marginBottom: "10px",
                  }}
                >
                  <TextField
                    size="small"
                    style={{ width: "22%", minWidth: "220px" }}
                    placeholder="Search player or happy hour..."
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    }}
                  />

                  <TextField
                    size="small"
                    label="From"
                    type="date"
                    value={filters.fromDate}
                    onChange={(e) => setFilter("fromDate", e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />

                  <TextField
                    size="small"
                    label="To"
                    type="date"
                    value={filters.toDate}
                    onChange={(e) => setFilter("toDate", e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />

                  <Button variant="outlined" size="medium" onClick={resetFilters}>
                    Reset
                  </Button>
                </div>

                <div className="row">
                  <div className="col-md-12">
                    <TableContainer component={Paper}>
                      <Table
                        className="c_table"
                        size="small"
                        stickyHeader
                        sx={{ minWidth: 500 }}
                        aria-label="happy hour earnings table"
                      >
                        <TableHead>
                          <TableRow>
                            {COLUMNS.map((column) => (
                              <TableCell key={column.id} align="center">
                                <TableSortLabel
                                  active={sortBy === column.id}
                                  direction={sortBy === column.id ? sortDir : "asc"}
                                  onClick={() => handleSort(column.id)}
                                >
                                  {column.label}
                                </TableSortLabel>
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {pagedRows.map((row) => (
                            <TableRow
                              key={row.id}
                              sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                            >
                              <TableCell align="center">{row.id}</TableCell>
                              <TableCell align="center">
                                {row.happyHourTitle || `#${row.happyHourId}`}
                              </TableCell>
                              <TableCell align="center">
                                {row.login || `#${row.userId}`}
                              </TableCell>
                              <TableCell align="center">{row.email || "-"}</TableCell>
                              <TableCell align="center">{formatDate(row.occurrenceDate)}</TableCell>
                              <TableCell align="center">{formatNumber(row.challengesPlayed)}</TableCell>
                              <TableCell align="center">{formatNumber(row.baseCoins)}</TableCell>
                              <TableCell align="center">{formatNumber(row.bonusCoins)}</TableCell>
                              <TableCell align="center">{formatNumber(row.totalCoins)}</TableCell>
                              <TableCell align="center">
                                <Chip
                                  size="small"
                                  label={row.bonusDropClaimed === 1 ? "Claimed" : "No"}
                                  color={row.bonusDropClaimed === 1 ? "success" : "default"}
                                />
                              </TableCell>
                              <TableCell align="center">{formatDateTime(row.updatedOn)}</TableCell>
                            </TableRow>
                          ))}

                          {!isLoading && pagedRows.length === 0 && (
                            <TableRow>
                              <TableCell colSpan={COLUMNS.length} align="center">
                                No earnings found for the selected filters.
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                        <TableFooter>
                          <TableRow>
                            <TablePagination
                              rowsPerPageOptions={[10, 25, 50, { label: "All", value: -1 }]}
                              colSpan={COLUMNS.length}
                              count={rows.length}
                              rowsPerPage={rowsPerPage}
                              page={page}
                              SelectProps={{
                                inputProps: { "aria-label": "rows per page" },
                                native: true,
                              }}
                              onPageChange={handleChangePage}
                              onRowsPerPageChange={handleChangeRowsPerPage}
                              ActionsComponent={TablePaginationActions}
                            />
                          </TableRow>
                        </TableFooter>
                      </Table>
                    </TableContainer>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default HappyHourEarnings;
